import { useNavigate } from 'react-router-dom';
import { Camera, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PhotoThumbnail, usePhotoLightbox, getMediaDetailPath } from '@/components/ui/PhotoGallery';
import { useTranslation } from 'react-i18next';

/**
 * Rendu d'un champ photo avec lightbox (mode create : sans suppression)
 */
export function PhotoFieldCreate({ field, photos, onPhotoCapture, isUploading }) {
    const { openLightbox } = usePhotoLightbox(photos);
    const minPhotos = field.minPhotos || field.options?.min || 1;
    const maxPhotos = field.maxPhotos || field.options?.max || 6;
    const canAddMore = photos.length < maxPhotos;

    return (
        <div>
            <PhotoFieldHeader field={field} photos={photos} minPhotos={minPhotos} maxPhotos={maxPhotos} />

            {photos.length > 0 && (
                <div className="flex flex-wrap gap-[12px] mb-[16px]">
                    {photos.map((photo, i) => (
                        <PhotoThumbnail
                            key={photo.uniq_id || i}
                            photo={photo}
                            index={i}
                            showRemoveButton={false}
                            showHorodateBadge={true}
                            onClick={photo.uniq_id ? openLightbox : undefined}
                            width={200}
                            height={200}
                        />
                    ))}
                </div>
            )}

            {canAddMore && <PhotoAddButton isUploading={isUploading} onClick={() => onPhotoCapture(field.id)} />}
        </div>
    );
}

/**
 * Rendu d'un champ photo avec suppression et lien viewer (mode edit)
 */
export function PhotoFieldEdit({ field, photos, onPhotoCapture, onRemovePhoto, isUploading, sourceUrl }) {
    const navigate = useNavigate();
    const minPhotos = field.minPhotos || 1;
    const maxPhotos = field.maxPhotos || 6;
    const canAddMore = photos.length < maxPhotos;

    const handlePhotoClick = (photo) => {
        const path = getMediaDetailPath(photo);
        if (path) {
            navigate(path, { state: { from: sourceUrl } });
        }
    };

    return (
        <div>
            <PhotoFieldHeader field={field} photos={photos} minPhotos={minPhotos} maxPhotos={maxPhotos} />

            {photos.length > 0 && (
                <div className="flex flex-wrap gap-[12px] mb-[16px]">
                    {photos.map((photo, i) => (
                        <PhotoThumbnail
                            key={photo.uniq_id || i}
                            photo={photo}
                            index={i}
                            showRemoveButton={true}
                            onRemove={() => onRemovePhoto(field.id, i)}
                            showHorodateBadge={true}
                            onClick={photo.uniq_id ? () => handlePhotoClick(photo) : undefined}
                            width={200}
                            height={200}
                        />
                    ))}
                </div>
            )}

            {canAddMore && <PhotoAddButton isUploading={isUploading} onClick={() => onPhotoCapture(field.id)} />}
        </div>
    );
}

function PhotoFieldHeader({ field, photos, minPhotos, maxPhotos }) {
    const { t } = useTranslation();
    return (
        <div className="flex items-center justify-between mb-[12px]">
            <div className="flex-1">
                <div className="flex items-center gap-[8px] mb-[4px]">
                    <h3 data-required={field.required || undefined} className="text-[15px] text-foreground">
                        {field.label}
                    </h3>
                    <span className="bg-secondary text-secondary-foreground rounded-[6px] px-[8px] py-[2px] text-[11px] font-medium">
                        {t('procedures:1JetonPhoto')}
                    </span>
                </div>
                <p className="text-[12px] text-muted-foreground">
                    {t('procedures:minimum')} {minPhotos} photo{minPhotos > 1 ? 's' : ''}
                </p>
            </div>
            <span className="text-[13px] text-muted-foreground">
                {photos.length}/{maxPhotos}
            </span>
        </div>
    );
}

function PhotoAddButton({ isUploading, onClick }) {
    const { t } = useTranslation();
    return (
        <div>
            <Button
                type="button"
                variant="plain"
                color="secondary"
                size="cta"
                onClick={onClick}
                disabled={isUploading}
                className="w-full h-auto border-2 border-dashed border-secondary bg-secondary/5 rounded-[12px] py-[20px] transition-colors disabled:opacity-50 flex flex-col items-center"
            >
                {isUploading ? (
                    <Loader2 className="h-6 w-6 text-secondary mb-2 animate-spin" />
                ) : (
                    <Camera className="h-6 w-6 text-secondary mb-2" />
                )}
                <p className="text-[14px] text-secondary">
                    {isUploading ? t('procedures:envoiEnCours') : t('procedures:prendreUnePhotoHorodatee')}
                </p>
            </Button>
            <p className="text-[13px] text-muted-foreground text-center mt-[8px]">
                {t('procedures:importantChaquePriseDeVueConsommeraImmediatement')}
            </p>
        </div>
    );
}
